import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { Loader2, Crown, ArrowLeft, Sparkles } from "lucide-react";
import ProBadge from "@/components/ProBadge";

export default function ProMembers() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/pro/members?limit=100")
      .then(r => setMembers(r.data?.members || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12 relative">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(176,38,255,0.35), transparent 60%)" }}
      />
      <div className="max-w-4xl mx-auto px-6 pt-10 relative">
        <button
          data-testid="pro-members-back-btn"
          onClick={() => nav("/pro")}
          className="text-white/50 hover:text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> back to pro
        </button>

        <header className="mb-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF] mb-2">
            // pro.members
          </p>
          <h1
            className="font-display font-black text-5xl md:text-6xl flex items-center gap-3 glitch mb-3"
            data-text="THE PRO CIRCLE"
          >
            <Crown className="h-8 w-8 text-[#B026FF]" strokeWidth={2.2} />
            THE PRO CIRCLE
          </h1>
          <p className="text-white/50 text-sm font-mono">
            Everyone flying with a MatrixShopping Pro pass. Join them → <span onClick={() => nav("/pro")} className="text-[#B026FF] underline cursor-pointer">/pro</span>
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 text-[#B026FF] animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0C] p-10 text-center">
            <Sparkles className="h-8 w-8 text-[#B026FF] mx-auto mb-3" />
            <p className="font-display font-black text-2xl text-white mb-2">Be the first.</p>
            <p className="text-white/50 text-sm font-mono">No one's gone Pro yet — front-row seat available.</p>
            <button
              onClick={() => nav("/pro")}
              data-testid="pro-members-cta-btn"
              className="mt-5 bg-[#B026FF] text-white font-bold uppercase tracking-widest text-xs px-6 py-3 hover:shadow-[0_0_20px_rgba(176,38,255,0.6)] transition-all"
            >
              claim the first slot
            </button>
          </div>
        ) : (
          <div data-testid="pro-members-grid" className="grid gap-3 sm:grid-cols-2">
            {members.map((m) => (
              <button
                key={m.user_id}
                data-testid={`pro-member-${m.username}`}
                onClick={() => nav(`/u/${m.username}`)}
                className="text-left flex items-center gap-4 p-4 border border-[#B026FF]/25 bg-gradient-to-br from-[#B026FF]/8 via-black to-black hover:border-[#B026FF]/60 hover:shadow-[0_0_18px_rgba(176,38,255,0.35)] transition-all"
              >
                {m.picture ? (
                  <img src={picUrl(m.picture)} alt="" className="h-12 w-12 object-cover border border-[#B026FF]/40" />
                ) : (
                  <div className="h-12 w-12 bg-[#B026FF]/20 flex items-center justify-center font-display font-bold text-[#B026FF]">
                    {m.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5">
                    <span className="text-white font-semibold truncate">{m.name}</span>
                    <ProBadge isPro size="xs" />
                  </p>
                  <p className="font-mono text-[10px] text-white/40 truncate">@{m.username}</p>
                  {m.bio && <p className="text-white/50 text-xs mt-1 truncate">{m.bio}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
